import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { MerchantAccountType } from '@/api/client';
import type { MerchantView, Product } from '@/api/types';
import { Card, ErrorNote, Spinner, Timestamp } from '@/components/Common';

interface CheckoutRequest {
  id: string;
  merchantId: string;
  merchantCode: string | null;
  merchantAccountType: MerchantAccountType | null;
  productId: number;
  productCode: string | null;
  target: string;
  amount: string;
  currency: string;
  customerMsisdn: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
  orderId: string | null;
  failureReason: string | null;
  paymentMethod: 'ECOCASH' | 'CASH' | 'BANK_TRANSFER';
  payerReference: string | null;
  createdBy: string | null;
  createdAt: string;
}

const STATUS_STYLE: Record<CheckoutRequest['status'], string> = {
  PENDING: 'bg-amber-100 text-amber-900',
  PAID: 'bg-emerald-100 text-emerald-800',
  FAILED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-ink-200 text-ink-600',
};

/**
 * A company/agent contacts you directly, pays by cash or bank transfer, and you log the request
 * here — then confirm it once you've verified the money actually arrived, which places the
 * recharge. Distinct from the public /pay page, which is EcoCash-only and self-service.
 */
export function CompanyRequestsPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [merchantFilter, setMerchantFilter] = useState('');

  const { data: merchantsData } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });
  const merchantOptions = [...(merchantsData?.merchants ?? [])].sort((a, b) => a.code.localeCompare(b.code));

  const { data, isLoading, error } = useQuery({
    queryKey: ['checkout-requests', merchantFilter],
    queryFn: () => api.get<{ requests: CheckoutRequest[] }>(
      `/api/admin/checkout${merchantFilter ? `?merchantId=${merchantFilter}` : ''}`),
    refetchInterval: 15_000,
  });

  const confirm = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/checkout/${id}/confirm`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checkout-requests'] }),
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/checkout/${id}/reject?reason=${encodeURIComponent('Payment not received')}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checkout-requests'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const requests = data?.requests ?? [];

  return (
    <div className="space-y-4">
      <Card
        title={`Checkout requests — ${requests.length}`}
        actions={
          <div className="flex items-center gap-2">
            <select className="input" value={merchantFilter}
                    onChange={(e) => setMerchantFilter(e.target.value)}>
              <option value="">All merchants</option>
              {merchantOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code}{m.accountType && m.accountType !== 'GENERAL' ? ` (${m.accountType})` : ''}
                </option>
              ))}
            </select>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              Log a request
            </button>
          </div>
        }
      >
        <p className="border-b border-ink-100 px-4 py-2 text-xs text-ink-500">
          Every checkout session across every channel — the public pay page, vendor till sales, and
          company requests logged here by staff.
        </p>
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Status</th><th className="th">Merchant</th>
              <th className="th">Method</th>
              <th className="th">Reference</th><th className="th">Product</th>
              <th className="th">Target</th><th className="th text-right">Amount</th>
              <th className="th">By</th>
              <th className="th">Logged</th><th className="th" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {requests.map((r) => (
              <tr key={r.id}>
                <td className="td">
                  <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>
                    {r.status}
                  </span>
                </td>
                <td className="td text-ink-600">
                  {r.merchantCode ?? '—'}
                  {r.merchantAccountType && r.merchantAccountType !== 'GENERAL' && (
                    <span className="ml-1 text-xs text-ink-400">({r.merchantAccountType})</span>
                  )}
                </td>
                <td className="td text-xs text-ink-600">{r.paymentMethod.replace('_', ' ')}</td>
                <td className="td text-xs text-ink-600">{r.payerReference ?? '—'}</td>
                <td className="td tabular text-ink-600">{r.productId}{r.productCode ? ` (${r.productCode})` : ''}</td>
                <td className="td mono">{r.target}</td>
                <td className="td text-right tabular">{r.currency} {r.amount}</td>
                <td className="td text-xs text-ink-600">{r.createdBy ?? '—'}</td>
                <td className="td"><Timestamp value={r.createdAt} /></td>
                <td className="td text-right">
                  {r.status === 'PENDING' && (
                    <div className="flex justify-end gap-2">
                      <button type="button" className="btn-secondary text-red-700"
                              disabled={reject.isPending}
                              onClick={() => reject.mutate(r.id)}>
                        Reject
                      </button>
                      <button type="button" className="btn-primary"
                              disabled={confirm.isPending}
                              onClick={() => confirm.mutate(r.id)}>
                        Confirm paid
                      </button>
                    </div>
                  )}
                  {r.status === 'FAILED' && r.failureReason && (
                    <span className="text-xs text-ink-500">{r.failureReason}</span>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr><td className="td py-8 text-center text-ink-500" colSpan={10}>No requests logged yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
        {(confirm.isError || reject.isError) && <ErrorNote error={confirm.error ?? reject.error} />}
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          Only confirm once you have actually verified the cash or bank transfer arrived — confirming
          places the real recharge immediately, and it cannot be undone from here.
        </p>
      </Card>

      {creating && <CreateRequestModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateRequestModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [merchantId, setMerchantId] = useState('');
  const [productId, setProductId] = useState('');
  const [productCode, setProductCode] = useState('');
  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'BANK_TRANSFER'>('BANK_TRANSFER');
  const [payerReference, setPayerReference] = useState('');

  const { data: merchantsData } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });
  const { data: catalogueData } = useQuery({
    queryKey: ['catalogue'],
    queryFn: () => api.get<{ products: Product[] }>('/api/admin/catalogue/products'),
  });

  const products = (catalogueData?.products ?? []).filter((p) =>
    p.enabled && /mobile|msisdn|cellular/i.test(p.targetType ?? ''));
  const selected = products.find((p) => String(p.productId) === productId);

  const create = useMutation({
    mutationFn: () => api.post('/api/admin/checkout', {
      merchantId, productId: Number(productId), target, amount,
      productCode: productCode || undefined, contactNumber: contactNumber || undefined,
      paymentMethod, payerReference: payerReference || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkout-requests'] });
      onClose();
    },
  });

  const canSubmit = !!merchantId && !!productId && !!target && !!amount
    && (!selected?.requiresProductCode || !!productCode);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/40 p-6">
      <div className="card w-full max-w-lg">
        <header className="card-header flex items-center justify-between">
          <h2 className="text-sm font-semibold">Log a company request</h2>
          <button type="button" className="text-ink-400 hover:text-ink-700" onClick={onClose}>✕</button>
        </header>
        <div className="space-y-3 p-4">
          <label className="block">
            <span className="label">Merchant</span>
            <select className="input mt-1" value={merchantId} onChange={(e) => setMerchantId(e.target.value)}>
              <option value="">Select a merchant…</option>
              {(merchantsData?.merchants ?? []).map((m) => (
                <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="label">Product</span>
            <select className="input mt-1" value={productId}
                    onChange={(e) => { setProductId(e.target.value); setProductCode(''); }}>
              <option value="">Select a product…</option>
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>{p.productId} — {p.name}</option>
              ))}
            </select>
          </label>

          {selected?.requiresProductCode && (
            <label className="block">
              <span className="label">Bundle code</span>
              <input className="input mt-1" value={productCode} onChange={(e) => setProductCode(e.target.value)}
                     placeholder="From the Catalogue page's bundle list" />
            </label>
          )}

          <label className="block">
            <span className="label">Number to recharge</span>
            <input className="input mt-1" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0772929223" />
          </label>

          <label className="block">
            <span className="label">Amount{selected?.currency ? ` (${selected.currency})` : ''}</span>
            <input className="input mt-1 tabular" inputMode="decimal" value={amount}
                   onChange={(e) => setAmount(e.target.value)} placeholder="5.00" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="label">Payment method</span>
              <select className="input mt-1" value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as 'CASH' | 'BANK_TRANSFER')}>
                <option value="BANK_TRANSFER">Bank transfer</option>
                <option value="CASH">Cash</option>
              </select>
            </label>
            <label>
              <span className="label">Contact number (optional)</span>
              <input className="input mt-1" value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} />
            </label>
          </div>

          <label className="block">
            <span className="label">Reference (company name, transfer ref, etc.)</span>
            <input className="input mt-1" value={payerReference} onChange={(e) => setPayerReference(e.target.value)}
                   placeholder="e.g. Acme Traders — EcoCash txn ABC123" />
          </label>
        </div>

        {create.isError && <ErrorNote error={create.error} />}

        <div className="flex justify-end gap-2 border-t border-ink-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={!canSubmit || create.isPending}
                  onClick={() => create.mutate()}>
            {create.isPending ? 'Logging…' : 'Log request'}
          </button>
        </div>
      </div>
    </div>
  );
}
